import {
    AuthExpiredByType,
    CardEntry,
    ConfirmationType,
    EcommerceSecurityLevel,
    FilterMatcher,
    FilterOperator,
    LoadTransactionType,
    MessageType,
    PosEnvironment,
    TransactionExtendedType,
    TransactionFilterType,
    TransferSourceChannel,
    WalletType,
} from '@lib/enums';
import { ChargeType } from './charge.interface';

export interface ITransactionFilterPayload {
    queryObjectType: string;
    fieldName: string;
    fieldValue: string;
}
export interface ITransactionFilter {
    field: string;
    operator: FilterOperator;
    values: Array<string>;
}

export interface ITransaction {
    amount: string;
    count: string;
}

export interface ITransactionStatus {
    approved: ITransaction;
    declined: ITransaction;
    settled: ITransaction;
    value: string;
}

export enum TransactionStatusEnum {
    APPORVED = 'Approved',
    DECLINED = 'Declined',
    SETTLED = 'Settled',
}

export interface IFeeDetail {
    amount: number | { $numberDecimal: string };
    currency?: string;
    description: string;
    fee_id: string;
    is_advanced?: boolean;
    transaction_type?: string;
}

export interface ITaxDetail {
    tax_id: string;
    tax_name: string;
    description: string;
    computed_tax_amount: number;
    percentage: number;
    clusivity: string;
    category: string;
    type: string;
}

export interface IFee {
    active: boolean;
    amount: number;
    created: Date;
    currency: string;
    description: string;
    name?: string;
    id: string;
    modified: Date;
    percentage?: number;
    range: any[];
    conditions?: any[];
    source_channel: string;
    transaction_origin: string;
    transaction_type: string;
    transfer_type: any[];
    type: string;
}

export interface ISweepDetail {
    debit_from_currency: string;
    debit_from_amount: number;
    credit_to_currency: string;
    credit_to_amount: number;
    exchange_rate: number;
    markup_amount: number;
}

export interface ITransactionAdvanceFilter {
    id: string;
    card_id: string;
    user_id: string;
    account_id1: string;
    account_id2: string;
    card_product_1: string;
    card_product_2: string;
    billing_currency_account: string;
    parent_transaction_id: string;
    status_code: string;
    type: TransactionFilterType;
    transaction_type: TransactionExtendedType | ChargeType;
    message_type: MessageType;
    transfer_id: string;
    transmission_date_time: string;
    auth_expired_by: AuthExpiredByType;
    network_transaction_id: string;
    auth_expired_at: string;
    rrn: string;
    sender_user_id: string;
    receiver_user_id: string;
    card_last4_digits: string;
    transaction_amount: number;
    transaction_currency: string;
    mcc: string;
    billing_amount: number;
    merchant_name: string;
    billing_currency: string;
    to_currency: string;
}

export interface ITransactionIndex {
    id: string;
    transaction_timestamp: string;
    parent_transaction_id: string;
    account_balance_details: IAccountDetail[];
    network: string;
    message_type: MessageType;
    transaction_type: TransactionExtendedType;
    transaction_description: string;
    transmission_date_time: string;
    date_time_acquirer: string;
    card_id: string;
    card_first6_digits: string;
    card_last4_digits: string;
    card_expiry_date: string;
    user_id: string;
    account_id1: string;
    card_product1: string;
    account_id2: string;
    card_product2: string;
    acquirer_id: string;
    merchant_id: string;
    mcc: string;
    merchant_name: string;
    merchant_city: string;
    merchant_country: string;
    terminal_id: string;
    stan: string;
    rrn: string;
    auth_id_response: string;
    network_transaction_id: string;
    transaction_amount: number;
    transaction_currency: string;
    billing_amount: number;
    billing_currency: string;
    billing_amount_account: number;
    billing_currency_account: string;
    conversion_rate_billing_account: number;
    acquirer_fee_amount: number;
    conversion_rate_billing: string;
    fee_amount: number;
    fee_details: Array<IFeeDetail>;
    tax_amount?: number;
    fee_tax_amount?: number;
    tax_details?: Array<ITaxDetail>;
    original_amount: number;
    original_amount_billing: string;
    status_code: string;
    status_description: string;
    transfer_id: string;
    load_id: string;
    unload_id: string;
    eci: string;
    ecommerce_security_level: EcommerceSecurityLevel;
    card_entry: CardEntry;
    pos_environment: PosEnvironment;
    fallback: boolean;
    pin_present: boolean;
    moto: boolean;
    recurring: boolean;
    installment_transaction: boolean;
    auth_expired_at: string;
    auth_expired_by: AuthExpiredByType;
    is_reversed: ConfirmationType;
    is_settled: ConfirmationType;
    source_channel: TransferSourceChannel;
    notes: string;
    sender_user_id: string;
    external_reference_number: string;
    external_reference_date: string;
    reference_number: string;
    sweep_details: Array<ISweepDetail>;
    type: LoadTransactionType;
    quote_id: string;
    receiver_user_id: string;
    senderCardLast4: string;
    receiverCardLast4: string;
    sender_name: string;
    receiver_name: string;
    card_product1_id?: string;
    card_product2_id?: string;
    to_amount?: number;
    to_currency?: string;
    exchange_rate?: number;
    reversal_count?: number;
    clearing_count?: number;
    is_cancelled?: string;
    incremental_transaction?: string;
    is_cash_advance?: string;
    transfer_type?: string;
    external_transaction_sub_statuses?: Array<IExternalTransactionSubStatus>;
    external_data_to_collect?: Array<IExternalDataToCollect>;
    account_details: IAccountDetail[];
    custom_info?: Record<string, any>;
    charge_type?: ChargeType;
}

export interface IExternalTransactionSubStatus {
    description: string;
    sub_status: string;
    target_customer: 'Sender' | 'Receiver' | string;
}

export interface IExternalDataToCollect {
    data_collection: string;
}

export interface IAccountDetail {
    id: string;
    balances: IAccountsBalance[];
}

export interface IAccountsBalance {
    currency: string;
    available_balance: number;
    ledger_balance: number;
}

export interface ICursor {
    after?: string;
    has_more?: boolean;
}

export interface ISearchTransactionRequest {
    query: string;
    sort_by: string;
    order_by: string;
    matcher: FilterMatcher;
    filters: Array<ITransactionFilter>;
    cursors: ICursor;
    limit: number;
}

export interface ISearchTransactionRequestPayload {
    query: string;
    sort_by: string;
    order_by: string;
    matcher: FilterMatcher;
    boolean_query?: IBooleanQueryPayload;
    filters?: Array<ITransactionFilter>;
    cursors: ICursor;
    limit: number;
}

export interface IBooleanQueryPayload {
    must?: Array<ITransactionFilterPayload | ISearchBooleanQueryMust>;
    must_not?: Array<ISearchBooleanQueryMust>;
}

export interface IPaging {
    cursors: ICursor;
    has_more?: boolean;
}

export interface ISearchTransactionResponse {
    paging?: IPaging;
    data: Array<ITransactionIndex>;
}

export interface ILinkFeeForm {
    fee: string;
}

export interface ISearchCashbackWalletRequest {
    cursors: ICursor;
    sort_by: string;
    order_by: string;
    limit: number;
    boolean_query: ISearchBooleanQuery;
    filters?: Array<ITransactionFilter>;
}

export interface ISearchBooleanQuery {
    must: Array<ISearchBooleanQueryMust>;
}

export interface ISearchBooleanQueryMust {
    queryObjectType: string;
    bool: ISearchBooleanQueryMustBool;
}

export interface ISearchBooleanQueryMustBool {
    should?: Array<ISearchBooleanQueryMustBoolShould>;
    must_not?: Array<ISearchBooleanQueryMustBoolShould | ISearchBooleanQueryNestedBool>;
}

export interface ISearchBooleanQueryNestedBool {
    queryObjectType: string;
    bool: { must: Array<ISearchBooleanQueryMustBoolShould> };
}

export interface ISearchBooleanQueryMustBoolShould {
    queryObjectType: string;
    fieldName: string;
    fieldValue: string;
}

export interface IWalletIndex {
    id: string;
    to_amount: number;
    from_amount: number;
    to_currency: string;
    from_currency: string;
    transaction_timestamp: string;
    sender_account_id: string;
    sender_name: string;
    sender_user_id: string;
    sender_card_last4: string;
    type: WalletType;
    merchant_name: string;
    sku: string;
    sku_name: string;
    payment_mode: string;
    status_code: string;
    rrn: string;
}

export interface ICashbackIndex {
    id: string;
    parent_transaction_id: string;
    to_amount: number;
    from_amount: number;
    to_currency: string;
    from_currency: string;
    fee_amount: number;
    transaction_timestamp: string;
    recipient_account_id: string;
    recipient_user_id: string;
    type: string;
    parent_transaction_type: string;
    status_code: string;
    rrn: string;
}

export interface ISearchWalletResponse {
    paging?: IPaging;
    data: Array<IWalletIndex>;
}

export interface ISearchCashbackResponse {
    paging?: IPaging;
    data: Array<ICashbackIndex>;
}

export const FeeTypes = [
    { key: 'FLAT', value: 'Flat' },
    { key: 'PERCENTAGE', value: 'Percentage' },
];

export const AdvancedFeeTypes = [
    { key: 'FLAT', value: 'Flat' },
    { key: 'PERCENTAGE', value: 'Percentage' },
    { key: 'MAX_PERC_OR_FLAT', value: 'Max Percentage or Flat' },
    { key: 'MIN_PERC_OR_FLAT', value: 'Min Percentage or Flat' },
    { key: 'MAX_PERC_OR_FLAT_W_CAP', value: 'Max Percentage or Flat With Capped Amount' },
    { key: 'MIN_PERC_OR_FLAT_W_CAP', value: 'Min Percentage or Flat With Capped Amount' },
    { key: 'RANGE', value: 'Range' },
    { key: 'RANGE_FLAT', value: 'Range Flat' },
    { key: 'RANGE_PERCENTAGE', value: 'Range Percentage' },
    { key: 'RANGE_MAX_PERC_OR_FLAT', value: 'Range Max Percentage Or Flat' },
    { key: 'RANGE_MIN_PERC_OR_FLAT', value: 'Range Min Percentage Or Flat' },
    { key: 'RANGE_MAX_PERC_OR_FLAT_W_CAP', value: 'Range Max Percentage Or Flat With Capped Amount' },
    { key: 'RANGE_MIN_PERC_OR_FLAT_W_CAP', value: 'Range Min Percentage Or Flat With Capped Amount' },
];

export const TransactionTypes = [
    { key: 'PURCHASES', value: 'Purchases' },
    { key: 'WITHDRAWALS', value: 'Withdrawals' },
    { key: 'BALANCE_INQUIRIES', value: 'Balance Inquiries' },
    { key: 'TRANSFERS', value: 'Transfers' },
    { key: 'LOADS', value: 'Loads' },
    { key: 'UNLOADS', value: 'Unloads' },
    { key: 'PURCHASE_FOREIGN_CURRENCY', value: 'Purchase Foreign Currency' },
    { key: 'CURRENCY_MARKUP', value: 'Currency Markup' },
    { key: 'SCHEME_CREDIT', value: 'Scheme Credit' },
    { key: 'OCT', value: 'OCT' },
    { key: 'ATM_DEPOSIT', value: 'ATM Deposit' },
    { key: 'ATM_DEPOSIT_FOREIGN_CURRENCY', value: 'ATM Deposit Foreign Currency' },
    { key: 'CASH_IN', value: 'Cash In' },
    { key: 'CASH_IN_FOREIGN_CURRENCY', value: 'Cash In Foreign Currency' },
    { key: 'CASH_OUT', value: 'Cash Out' },
    { key: 'CASH_OUT_FOREIGN_CURRENCY', value: 'Cash Out Foreign Currency' },
];

export const TransactionOrigins = [
    { key: 'DOMESTIC', value: 'Domestic' },
    { key: 'FOREIGN', value: 'Foreign' },
    { key: 'ANY', value: 'Any' },
];

export const SourceChannels = [
    { key: 'ANY', value: 'Any' },
    { key: 'MPGS', value: 'MPGS' },
    { key: 'DAPI', value: 'DAPI' },
    { key: 'LEAN', value: 'LEAN' },
];
export const WalletTypes = [
    {
        queryObjectType: 'match',
        fieldName: 'type',
        fieldValue: 'FUNDS_TRANSFER',
    },
    {
        queryObjectType: 'match',
        fieldName: 'type',
        fieldValue: 'BILL_PAYMENT',
    },
    {
        queryObjectType: 'match',
        fieldName: 'type',
        fieldValue: 'CURRENCY_EXCHANGE',
    },
    {
        queryObjectType: 'match',
        fieldName: 'type',
        fieldValue: 'LOAD_CHECKOUT',
    },
    {
        queryObjectType: 'match',
        fieldName: 'type',
        fieldValue: 'LOAD_LEAN',
    },
    {
        queryObjectType: 'match',
        fieldName: 'type',
        fieldValue: 'REMITTANCES',
    },
    {
        queryObjectType: 'match',
        fieldName: 'load_type',
        fieldValue: 'COMPLETION',
    },
    {
        queryObjectType: 'match',
        fieldName: 'status_code',
        fieldValue: '0000',
    },
    {
        queryObjectType: 'match',
        fieldName: 'load_type',
        fieldValue: 'POST',
    },
    {
        queryObjectType: 'match',
        fieldName: 'load_type',
        fieldValue: 'HOLD',
    },
    {
        queryObjectType: 'match',
        fieldName: 'message_type',
        fieldValue: 'AUTHORIZATION',
    },
];

export const WalletQRTypes = [
    {
        queryObjectType: 'match',
        fieldName: 'type',
        fieldValue: 'QR_PAYMENTS',
    },
    {
        queryObjectType: 'match',
        fieldName: 'type',
        fieldValue: 'QR_PAYMENTS_REFUND',
    },
    {
        queryObjectType: 'match',
        fieldName: 'type',
        fieldValue: 'NCPGS_TRANSFER',
    },
    {
        queryObjectType: 'match',
        fieldName: 'type',
        fieldValue: 'FUNDS_TRANSFER',
    },
];

export interface AmountDetail {
    amount_type: string;
    amount: number;
    amount_currency: string;
}

export interface SendAmounts {
    send_amount: number;
    send_currency: string;
    total_send_fees: number;
    total_discount_amount: number;
    total_send_taxes: number;
    total_paid_amount: number;
    detail_send_amounts: AmountDetail[];
    total_amount_to_collect: number;
}

export interface ReceiveAmounts {
    receive_amount: number;
    receive_currency: string;
    valid_currency_indicator: boolean;
    payout_currency: string;
    total_receive_fees: number;
    total_receive_taxes: number;
    total_receive_amount: number;
    receive_fees_are_estimated: boolean;
    receive_taxes_are_estimated: boolean;
    detail_estimated_receive_amounts: AmountDetail[];
    detail_receive_amounts: AmountDetail[];
}

export interface ConvertedSendAmounts {
    send_amount: number;
    send_currency: string;
    total_discount_amount: number;
    mg_fees: number;
    platform_fees: number;
    total_paid_amount: number;
}

export interface PromotionInfo {
    promotion_code: string;
    promotion_discount: number;
    promotion_discount_amount: number;
    promotion_discount_id: string;
    promotion_error_message: string[];
}

export interface IMGITransactionIndex {
    mgi_transaction_session_id: string;
    transaction_status: string;
    date_time_sent: string;
    receive_country: string;
    delivery_option: string;
    sender_first_name: string;
    sender_last_name: string;
    sender_address: string;
    sender_city: string;
    sender_country: string;
    sender_home_phone: string;
    receiver_address: string;
    receiver_phone: string;
    receiver_first_name: string;
    receiver_middle_name: string;
    receiver_last_name: string;
    sender_photo_id_type: string;
    sender_photo_id_number: string;
    sender_photo_id_country: string;
    sender_dob: string;
    sender_occupation: string;
    sender_birth_country: string;
    sender_passport_issue_country: string;
    valid_indicator: boolean;
    expected_date_of_delivery: string;
    redirect_indicator: boolean;
    promotion_info: PromotionInfo[];
    disclosure_text: any[];
    fee_refund_required: boolean;
    send_amounts: SendAmounts;
    receive_amounts: ReceiveAmounts;
    exchange_rate_applied: number;
    source_of_funds: string;
    relationship_to_receiver: string;
    send_purpose_of_transaction: string;
    total_paid_amount: number;
    source_of_funds_label: string;
    relationship_to_receiver_label: string;
    send_purpose_of_transaction_label: string;
    converted_send_amounts: ConvertedSendAmounts;
}
